import { FeatureCollection, Geometry } from 'geojson';
import { Layer, Source } from 'shared/map-exports';
import { useMemo } from 'react';
import { useMapLayersState } from 'state/useMapLayersState';
import { ON_TOP_OF_EVERYTHING_LAYER_ID } from 'features/mapping/styles/constants';
import { FloodingListItem } from 'features/flooding/flooding.types';
import { getIconStyle, getLabelStyle } from './FloodingsLayer.styles';
import {
  FloodingFeatureProperties,
  FloodingsLayerProps,
  toGeojsonFeature,
} from './FloodingsLayer.utils';

export type FloodingsLayerConfig = {
  sourceId: string;
  iconsLayerId: string;
  labelsLayerId: string;
  labelsMinZoom: number;
  /**
   * Predicate to filter flooding events rendered by this layer.
   * Must be a stable reference (e.g. a module-level constant) to avoid
   * recomputing the GeoJSON memo on every render.
   */
  filterEvent: (event: FloodingListItem) => boolean;
};

type FloodingsLayerBaseProps = FloodingsLayerProps & {
  config: FloodingsLayerConfig;
};

export const FloodingsLayerBase = (
  props: FloodingsLayerBaseProps,
): JSX.Element => {
  const {
    floodingEvents,
    selectedGeoEventId = 0,
    interactive = true,
    isFadable = false,
    emphasizedGeoEventIds,
    isEdit = false,
    config,
  } = props;
  const { isLightBaseLayer } = useMapLayersState();

  const geojson: FeatureCollection<Geometry, FloodingFeatureProperties> =
    useMemo(
      () => ({
        type: 'FeatureCollection',
        features: floodingEvents
          .filter(config.filterEvent)
          .map((e) => toGeojsonFeature(e, isFadable, emphasizedGeoEventIds)),
      }),
      [floodingEvents, isFadable, emphasizedGeoEventIds, config.filterEvent],
    );

  return (
    <Source id={config.sourceId} type="geojson" data={geojson}>
      <Layer
        id={config.iconsLayerId}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getIconStyle(selectedGeoEventId, isEdit)}
        metadata={{ interactive }}
      />
      <Layer
        id={config.labelsLayerId}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getLabelStyle(
          isLightBaseLayer,
          selectedGeoEventId,
          config.labelsMinZoom,
        )}
        metadata={{ interactive }}
      />
    </Source>
  );
};
